import React, { useState } from "react";
import { Box, MenuItem, Tooltip, useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { BuildOutlined, ContentCopy, TuneOutlined, RestoreOutlined, ArchiveOutlined, GroupWork as GroupWorkIcon, PlaceOutlined } from "@mui/icons-material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import QrCodeIcon from "@mui/icons-material/QrCode";
import CameraIcon from "../../../shared/assets/icons/CameraIcon";
import TableActionButton from "../../../shared/components/tables/TableActionButton";
import { useConfirmation } from "../../../shared/components/confirmation/ConfirmationProvider";
import { Machine } from "../models/models";
import useTranslation from "../../../shared/hooks/useTranslation";
import PermissionGuard from "../../../shared/components/PermissionGuard";
import { Modules, PermissionLevel } from "../../../core/models/PermissionModels";
import {
  getMachineDisplayName,
  getMachineName,
} from "../../../shared/utils/machineUtils";
import { dt } from "../utils/dt";
import { ImagePreviewStrip, useGalleryImages } from "../../../shared/components/gallery";
import {
  ReadinessModel,
  ReadinessSummaryStrings,
  readinessSummaryLabel,
} from "../utils/midasReadiness";
import MidasReadinessPopup from "./readiness/MidasReadinessPopup";

// =====================================================================
//  HEALTH DOT COLOR
// =====================================================================

const getHealthColor = (openServiceReportsCount: number): string => {
  if (openServiceReportsCount === 0) return dt.machinery.health.green;
  if (openServiceReportsCount <= 2) return dt.machinery.health.yellow;
  return dt.machinery.health.red;
};

// =====================================================================
//  COMPONENT
// =====================================================================

interface CompactMachineCardProps {
  machine: Machine;
  showActions?: boolean;
  isNew?: boolean;
  /** DR-4 (item 7/8): opt-in for the minimal MIDAS readiness line. Default FALSE. */
  showMidasRow?: boolean;
  /** DR-4: live-connected — sourced once per page from the batch status fetch. */
  connected?: boolean;
  onQRCode?: (machine: Machine) => void;
  /* WhatsApp sunset 2026-03-14 — channel deactivated, web chat is sole channel */
  // onWhatsAppBot?: (machine: Machine) => void;
  onDecommission?: (machine: Machine) => void;
  onRestore?: (machine: Machine) => void;
  onEdit?: (machine: Machine) => void;
  onEditDetails?: (machine: Machine) => void;
  onCreateServiceRequest?: (machine: Machine) => void;
  onCreateServiceReport?: (machine: Machine) => void;
  onDuplicate?: (machine: Machine) => void;
  onAssignGroup?: (machine: Machine) => void;
}

const CompactMachineCard = ({
  machine,
  showActions,
  isNew,
  showMidasRow,
  connected,
  onQRCode,
  onEdit,
  onEditDetails,
  onCreateServiceRequest,
  onCreateServiceReport,
  onDuplicate,
  onDecommission,
  onRestore,
  onAssignGroup,
}: CompactMachineCardProps) => {
  const confirm = useConfirmation();
  const navigate = useNavigate();
  const { translate } = useTranslation("myMachinery");
  const { translate: tCommon } = useTranslation("common");
  const isPhone = useMediaQuery("(max-width:768px)");

  const [hovered, setHovered] = useState(false);
  const [midasOpen, setMidasOpen] = useState(false);

  const isClickable = showActions || false;
  const isDecommissioned = machine.isDecommissioned === true;

  // Item 7: minimal one-line MIDAS readiness. Uses the shared flat label (no
  // styled composite). Opt-in + only for catalog-linked, non-decommissioned
  // machines. CONNECTED → plain static text; otherwise a clickable link that
  // opens the shared popup.
  const catalogModel = machine.catalogModel;
  const showMidas = showMidasRow === true && !isDecommissioned && !!catalogModel;
  const midasModel: ReadinessModel | null = catalogModel
    ? { readiness: catalogModel.readiness, isOemPartner: catalogModel.isOemPartner === true, oemPartnerName: catalogModel.oemPartnerName }
    : null;
  const midasStrings: ReadinessSummaryStrings = {
    doc: translate("summaryDoc"),
    docs: translate("summaryDocs"),
    oem: translate("summaryOem"),
    oemAvailable: translate("summaryOemAvailable"),
    telemetryAvailable: translate("summaryTelemetryAvailable"),
    live: translate("summaryLive"),
  };
  const midasLabel = midasModel
    ? readinessSummaryLabel(midasModel, { connected: connected === true }, midasStrings)
    : null;

  const handleCardClick = () => {
    if (!isClickable) return;
    navigate(`/machinery/${machine.id}`);
  };

  const stopProp = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const healthColor = getHealthColor(machine.openServiceReportsCount);
  const healthTooltip =
    machine.openServiceReportsCount === 0
      ? translate("healthNoIssues")
      : translate("healthOpenReports", { count: machine.openServiceReportsCount });

  const displayName = getMachineDisplayName(machine);
  const subtitle = getMachineName(machine);
  const manufacturerName = machine?.manufacturer?.name || machine?.customBrandName || "";

  // Build one-line subtitle: type + manufacturer
  const subtitleParts: string[] = [];
  if (subtitle) subtitleParts.push(subtitle);
  if (manufacturerName && !displayName.toLowerCase().includes(manufacturerName.toLowerCase())) {
    subtitleParts.push(manufacturerName);
  }
  const oneLineSubtitle = subtitleParts.join(" \u00B7 ");

  // Location display
  const locationDisplay = machine.locationName
    ? machine.parentLocationName
      ? `${machine.parentLocationName} > ${machine.locationName}`
      : machine.locationName
    : null;

  // 3-tier image fallback: user upload > catalog > placeholder
  const galleryImages = useGalleryImages({
    type: "machine",
    image: machine?.image?.content || null,
    catalogImageUrl: machine?.catalogModel?.imageStandardUrl || machine?.catalogModel?.imageUrl || machine?.catalogImageUrl || null,
    catalogImages: machine?.catalogModel?.images || [],
    files: machine?.files?.map(f => ({ name: f.name, content: f.content })) || [],
  });
  const hasGalleryImages = galleryImages.length > 0;

  return (
    <Box
      onClick={handleCardClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: isPhone ? "10px" : "14px",
        padding: isPhone ? "8px 10px" : "8px 14px",
        minHeight: isPhone ? "60px" : "64px",
        boxSizing: "border-box",
        background: dt.machinery.card.bg,
        border: isDecommissioned ? dt.decommissioned.card.borderStyle : dt.machinery.card.border,
        borderRadius: dt.radiusSm,
        boxShadow: hovered ? dt.machinery.card.shadowHover : dt.machinery.card.shadow,
        fontFamily: dt.font,
        position: "relative",
        opacity: isDecommissioned ? dt.decommissioned.card.opacity : 1,
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        ...(isClickable
          ? {
              cursor: "pointer",
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: dt.machinery.card.shadowHover,
                opacity: isDecommissioned ? dt.decommissioned.card.opacityHover : 1,
              },
            }
          : {}),
      }}
    >
      {/* 48x48 Image */}
      <Box
        sx={{
          flexShrink: 0,
          filter: isDecommissioned ? dt.decommissioned.card.imageFilter : "none",
          transition: "filter 0.2s ease",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {hasGalleryImages ? (
          <ImagePreviewStrip
            images={galleryImages}
            maxVisible={1}
            thumbnailSize={48}
            gap={0}
            borderRadius="8px"
          />
        ) : (
          <Box
            sx={{
              width: "48px",
              height: "48px",
              minWidth: "48px",
              borderRadius: "8px",
              border: `1px solid ${dt.border.light}`,
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#F8FAFC",
            }}
          >
            <Box sx={{ transform: "scale(0.6)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <CameraIcon />
            </Box>
          </Box>
        )}
      </Box>

      {/* Name + subtitle */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: "2px",
        }}
      >
        <Box
          sx={{
            fontSize: "14px",
            fontWeight: 500,
            fontFamily: dt.font,
            color: dt.text.primary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            lineHeight: 1.3,
          }}
        >
          {displayName}
        </Box>
        {isDecommissioned ? (
          <Box sx={{ display: "flex", alignItems: "baseline", gap: "6px", minWidth: 0 }}>
            <Box
              sx={{
                fontSize: "11px",
                fontWeight: 600,
                fontFamily: dt.font,
                color: dt.decommissioned.badge.text,
                lineHeight: 1.3,
                flexShrink: 0,
              }}
            >
              {translate("decommissioned")}
            </Box>
            {machine.decommissionReason && (
              <Tooltip title={machine.decommissionReason} arrow placement="top">
                <Box sx={{
                  fontSize: "11px", fontFamily: dt.font, fontStyle: "italic",
                  color: dt.text.tertiary, lineHeight: 1.3,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0,
                }}>
                  &ldquo;{machine.decommissionReason}&rdquo;
                </Box>
              </Tooltip>
            )}
          </Box>
        ) : (
          <>
            {oneLineSubtitle && (
              <Box
                sx={{
                  fontSize: "12px",
                  fontWeight: 400,
                  fontFamily: dt.font,
                  color: dt.text.tertiary,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  lineHeight: 1.3,
                }}
              >
                {oneLineSubtitle}
              </Box>
            )}
            {locationDisplay && (
              <Box sx={{ display: "flex", alignItems: "center", gap: "3px", minWidth: 0 }}>
                <PlaceOutlined sx={{ fontSize: "11px", color: dt.blue, flexShrink: 0 }} />
                <Box sx={{
                  fontSize: "11px", fontFamily: dt.font, fontWeight: 400, color: dt.text.tertiary,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, lineHeight: 1.3,
                }}>
                  {locationDisplay}
                </Box>
              </Box>
            )}
            {/* Item 7: minimal MIDAS readiness line. */}
            {showMidas && (connected === true ? (
              midasLabel && (
                <Box sx={{
                  fontSize: "11px", fontFamily: dt.font, fontWeight: 500, color: dt.machinery.health.green,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, lineHeight: 1.3,
                }}>
                  {midasLabel}
                </Box>
              )
            ) : (
              <Box
                component="button"
                type="button"
                onClick={(e) => { e.stopPropagation(); setMidasOpen(true); }}
                aria-haspopup="dialog"
                aria-label={`${translate("midasRowLabel")}: ${midasLabel || translate("viewDetails")}`}
                sx={{
                  p: 0, m: 0, border: "none", background: "none", font: "inherit", textAlign: "left",
                  cursor: "pointer", maxWidth: "100%", display: "block",
                  fontSize: "11px", fontFamily: dt.font, fontWeight: 500, color: dt.blue, lineHeight: 1.3,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  "&:hover": { textDecoration: "underline" },
                  "&:focus-visible": { outline: `2px solid ${dt.blue}`, outlineOffset: "2px" },
                }}
              >
                {`${midasLabel || translate("viewDetails")} ›`}
              </Box>
            ))}
          </>
        )}
      </Box>

      {/* NEW badge -- inline flex child */}
      {isNew && (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: dt.machinery.newBadge.bg,
            color: dt.machinery.newBadge.text,
            border: `1px solid ${dt.machinery.newBadge.border}`,
            borderRadius: "8px",
            padding: "1px 8px",
            fontSize: "9px",
            fontWeight: 700,
            fontFamily: dt.font,
            textTransform: "uppercase",
            letterSpacing: "1px",
            lineHeight: "14px",
            whiteSpace: "nowrap",
            flexShrink: 0,
            animation: "newMachinePulse 2s ease-in-out 3",
          }}
        >
          {translate("new")}
        </Box>
      )}

      {/* Health dot -- hidden for decommissioned */}
      {showActions && !isDecommissioned && (
        <Tooltip title={healthTooltip} arrow placement="top">
          <Box
            sx={{
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              backgroundColor: healthColor,
              boxShadow: `0 0 6px ${healthColor}80`,
              flexShrink: 0,
              transition: "transform 0.2s ease",
              "&:hover": { transform: "scale(1.3)" },
            }}
          />
        </Tooltip>
      )}

      {/* Kebab menu -- visible on hover (always visible on phone/touch) */}
      {showActions && (
        <Box
          onClick={stopProp}
          sx={{
            opacity: isPhone ? 1 : hovered ? 1 : 0,
            transition: "opacity 0.15s ease",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
          }}
        >
          <TableActionButton stopPropagation>
            <MenuItem onClick={(e) => { e.stopPropagation(); navigate(`/machinery/${machine.id}`); }}>
              <TuneOutlined /> {translate("profile")}
            </MenuItem>
            {isDecommissioned ? (
              <MenuItem onClick={(e) => { e.stopPropagation(); onRestore?.(machine); }} sx={{ color: "#22C55E" }}>
                <RestoreOutlined sx={{ color: "#22C55E" }} /> {translate("restore")}
              </MenuItem>
            ) : (
              <>
                {/* QR -- Write+ since D7-FE1 (SEC-REQ-B7-34 option (i)): the
                    dialog now encodes the nameplate id-link from a
                    Machinery:Write GET. Do not ungate. */}
                <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
                  <MenuItem onClick={(e) => { e.stopPropagation(); onQRCode?.(machine); }}>
                    <QrCodeIcon /> {translate("qrCode")}
                  </MenuItem>
                </PermissionGuard>
                <MenuItem onClick={(e) => { e.stopPropagation(); onCreateServiceReport?.(machine); }}>
                  <BuildOutlined /> {translate("createServiceReport")}
                </MenuItem>
                <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
                  <MenuItem onClick={(e) => { e.stopPropagation(); onCreateServiceRequest?.(machine); }}>
                    <BuildOutlined /> {translate("createServiceRequest")}
                  </MenuItem>
                </PermissionGuard>
                <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
                  <MenuItem onClick={(e) => { e.stopPropagation(); (onEditDetails || onEdit)?.(machine); }}>
                    <EditOutlinedIcon /> {translate("editDetails")}
                  </MenuItem>
                </PermissionGuard>
                <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
                  <MenuItem onClick={(e) => { e.stopPropagation(); onDuplicate?.(machine); }}>
                    <ContentCopy /> {translate("duplicate")}
                  </MenuItem>
                </PermissionGuard>
                {onAssignGroup && (
                  <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
                    <MenuItem onClick={(e) => { e.stopPropagation(); onAssignGroup(machine); }}>
                      <GroupWorkIcon /> {translate("assignToGroup")}
                    </MenuItem>
                  </PermissionGuard>
                )}
                <PermissionGuard module={Modules.Machinery} level={PermissionLevel.Write}>
                  <MenuItem
                    onClick={(e) => { e.stopPropagation(); onDecommission?.(machine); }}
                    sx={{ color: dt.decommissioned.amber }}
                  >
                    <ArchiveOutlined sx={{ color: dt.decommissioned.amber }} /> {translate("decommission")}
                  </MenuItem>
                </PermissionGuard>
              </>
            )}
          </TableActionButton>
        </Box>
      )}

      {/* Shared readiness popup — opened by the minimal Midas line (item 7).
          Portaled by MUI; its Dialog stops click-propagation so opening/closing
          never triggers this card's navigate-to-profile onClick (item 1). */}
      {showMidas && midasModel && (
        <MidasReadinessPopup
          open={midasOpen}
          onClose={() => setMidasOpen(false)}
          readiness={midasModel.readiness}
          isOemPartner={midasModel.isOemPartner}
          oemPartnerName={midasModel.oemPartnerName}
          liveStatus={connected === true ? "connected" : "disconnected"}
          machineOrModelName={displayName}
          manufacturerName={catalogModel?.manufacturer?.name || machine.manufacturer?.name || null}
        />
      )}
    </Box>
  );
};

export default CompactMachineCard;
